import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppContext } from '@umss/shared/core';
import { AuthService, Contractor } from '@umss/shared/data-access';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom
} from 'rxjs';
import { ProfileService } from './profile.service';
import { showDevWarning } from '@umss/shared/utils';

@Injectable({ providedIn: 'root' })
export class ContractorContextService implements AppContext {
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);

  private readonly INITIAL_VALUE = '';

  /**
   * Contractor id that is computed based on the auth state.
   */
  readonly contractorId = computed(() => {
    return this.authService.authState().contractorId || this.INITIAL_VALUE;
  });

  /**
   * Flag used by the AuthRefresh provider to check if the ContractorContext has loaded.
   * It's marked as ready when it is different from the initial value.
   */
  readonly ready = computed(
    () => this.contractorId().trim() !== this.INITIAL_VALUE
  );

  /**
   * Contractor object of the logged-in user.
   * This is automatically loaded when the contractorId changes.
   */
  private _contractor = signal<Contractor | null>(null);
  readonly contractor = this._contractor.asReadonly();

  /**
   * Loading state for the contractor data.
   */
  private _loadingContractor = signal<boolean>(false);
  readonly loadingContractor = this._loadingContractor.asReadonly();

  // Shared in-flight promise so bootstrap (ensureLoaded) and the in-session
  // login subscription deduplicate when both fire for the same contractorId.
  private _loadInFlight: Promise<void> | null = null;

  // Computed signals for commonly used contractor properties
  readonly scopeOfPractice = computed(
    () => this._contractor()?.scopeOfPractice
  );

  readonly scopeOfPracticeDisplay = computed(
    () => this._contractor()?.scopeOfPracticeDisplay
  );

  readonly contractorName = computed(() => {
    const contractor = this._contractor();
    if (!contractor?.user) return undefined;
    return (
      contractor.user.fullName ||
      (contractor.user.firstName && contractor.user.lastName
        ? `${contractor.user.firstName} ${contractor.user.lastName}`
        : undefined)
    );
  });

  constructor() {
    // Handles in-session auth changes (e.g. AuthService.login / socialLogin
    // after the app is already running). Bootstrap is handled by ensureLoaded().
    const contractorId$ = toObservable(this.contractorId).pipe(
      filter((id) => !!id && id !== this.INITIAL_VALUE),
      distinctUntilChanged()
    );

    const hasToken$ = toObservable(
      computed(() => !!this.authService.accessToken)
    ).pipe(filter(Boolean));

    combineLatest([contractorId$, hasToken$])
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(([prevId], [currId]) => prevId === currId)
      )
      .subscribe(() => {
        this.loadContractor();
      });
  }

  /**
   * Synchronously inspects the auth state and triggers `getProfile()` if a
   * contractorId is present. Used by the bootstrap to avoid the
   * signal → toObservable → effect-flush race during `provideAppInitializer`.
   */
  ensureLoaded(): Promise<void> {
    const id = this.contractorId();
    showDevWarning('[ContractorContextService] ensureLoaded called', {
      contractorId: id,
      _contractor: this._contractor(),
      _loadInFlightExists: !!this._loadInFlight
    });
    if (!id) return Promise.resolve();
    if (this._contractor()?.id === id) return Promise.resolve();
    return this.loadContractor();
  }

  /**
   * Reloads the contractor data from the API.
   * Call this after operations that change contractor properties.
   */
  reloadContractor(): void {
    if (this.contractorId()) {
      // Force a fresh fetch even if cached.
      this._loadInFlight = null;
      this._contractor.set(null);
      this.loadContractor();
    }
  }

  private loadContractor(): Promise<void> {
    if (this._loadInFlight) return this._loadInFlight;

    this._loadingContractor.set(true);

    this._loadInFlight = firstValueFrom(this.profileService.getProfile())
      .then((contractor) => {
        this._contractor.set(contractor);
      })
      .catch(() => {
        this._contractor.set(null);
      })
      .finally(() => {
        this._loadingContractor.set(false);
        this._loadInFlight = null;
      });

    return this._loadInFlight;
  }

  update(updatedContractor: Contractor): void {
    this._contractor.set(updatedContractor);
  }
}
